import { RemoteParticipant, Room, RoomEvent, RoomOptions } from 'livekit-client';
import { LiveKitConfig } from '../LiveKitConfig';
import type {
  LiveKitConnectionOptions,
  LiveKitConnectionResult,
  LiveKitError,
  LiveKitEvents,
  LiveKitState,
} from '../types';
import { generateLiveKitToken } from '../utils/tokenGenerator';

/**
 * LiveKit Service for video streaming and joining
 *
 * Handles connection to LiveKit servers, manages room state, and publishes/subscribes to tracks.
 * This service wraps the LiveKit client SDK and provides a simplified interface.
 *
 * Key concepts:
 * - Room: Virtual space where participants meet
 * - Participant: A user in the room (local = you, remote = others)
 * - Track: Audio/video stream from a participant
 * - Publication: Metadata about a track (contains the actual track)
 */
export class LiveKitService {
  private room: Room | null = null;
  private state: LiveKitState = 'idle';
  private events: LiveKitEvents;

  constructor(events: LiveKitEvents = {}) {
    this.events = events;
  }

  /**
   * Connect to a LiveKit room
   */
  async connect(options: LiveKitConnectionOptions): Promise<LiveKitConnectionResult> {
    if (this.state !== 'idle') {
      console.warn(`Attempted to connect when state is "${this.state}". Ignoring.`);
      return {
        success: false,
        error: `Cannot connect when state is already "${this.state}".`,
      };
    }

    try {
      this.updateState('connecting');

      // --- START: Simple & Correct Quality Solution ---
      const roomOptions: RoomOptions = {
        // Enable adaptive streaming for all participants
        adaptiveStream: true,
        dynacast: true,
        // Add options for better multi-participant stability
        disconnectOnPageLeave: false,
        stopLocalTrackOnUnpublish: true,
      };

      // Apply high-quality streaming settings only for the 'stream' mode.
      // This is the key to ensuring viewers can receive a high-quality stream.
      if (options.mode === 'stream') {
        // 1. Set the camera capture resolution to 1080p.
        roomOptions.videoCaptureDefaults = {
          resolution: {
            width: 1920,
            height: 1080,
          },
          frameRate: 30,
        };
        // 2. Configure publishing options to broadcast multiple quality layers (simulcast).
        // Viewers will automatically subscribe to the best layer their network can handle.
        roomOptions.publishDefaults = {
          videoEncoding: {
            maxBitrate: 4_000_000, // 4 Mbps
            maxFramerate: 30,
          },
          simulcast: true,
        };
      }
      // --- END: Simple & Correct Quality Solution ---

      // Create room
      this.room = new Room(roomOptions);

      // Setup event listeners
      this.setupEventListeners();

      // Connect to room
      const token = await this.generateToken(
        options.roomName,
        options.participantName,
        options.mode
      );

      await this.room.connect(LiveKitConfig.url, token, {
        autoSubscribe: true,
        // Add connection options to improve stability
        websocketTimeout: 10000,
        // Configure RTC for better stability with multiple participants
        rtcConfig: {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
          ],
          iceCandidatePoolSize: 10,
        },
      });

      // Set participant name and log the assigned identity
      if (this.room.localParticipant) {
        this.room.localParticipant.setName(options.participantName);
        console.log('👤 Participant connected:', {
          identity: this.room.localParticipant.identity,
          mode: options.mode,
        });
      }

      // Publish local tracks if streaming
      if (options.mode === 'stream') {
        await this.publishLocalTracks();
      }

      this.updateState('connected');

      return {
        success: true,
        room: this.room,
        participant: this.room.localParticipant,
      };
    } catch (error: any) {
      const livekitError: LiveKitError = {
        type: 'connection',
        message: error.message || 'Failed to connect to LiveKit room',
        details: error,
      };
      this.handleError(livekitError);
      this.updateState('error');
      return {
        success: false,
        error: livekitError.message,
      };
    }
  }

  /**
   * Disconnect from the room
   */
  async disconnect(): Promise<void> {
    if (this.room) {
      await this.room.disconnect();
      this.room = null;
      this.updateState('disconnected');
    }
  }

  /**
   * Get the current room
   */
  getRoom(): Room | null {
    return this.room;
  }

  /**
   * Get the current state
   */
  getState(): LiveKitState {
    return this.state;
  }

  /**
   * Generate LiveKit token
   */
  private async generateToken(
    roomName: string,
    participantName: string,
    mode: string
  ): Promise<string> {
    // Generate token with appropriate permissions based on mode
    const canPublish = mode === 'stream'; // Only allow publishing for stream mode
    const canSubscribe = true; // Always allow subscribing

    return generateLiveKitToken(
      LiveKitConfig.apiKey,
      LiveKitConfig.apiSecret,
      roomName,
      participantName,
      {
        canPublish,
        canSubscribe,
        canPublishData: true,
      }
    );
  }

  /**
   * Publish local tracks (camera and microphone)
   */
  private async publishLocalTracks(): Promise<void> {
    if (!this.room?.localParticipant) {
      throw new Error('No local participant available');
    }
    try {
      await this.room.localParticipant.enableCameraAndMicrophone();

      // Log the actual quality being sent by the streamer for diagnostics.
      setTimeout(() => {
        const localParticipant = this.room?.localParticipant;
        if (localParticipant) {
          localParticipant.videoTrackPublications.forEach((publication) => {
            if (publication.track?.mediaStreamTrack) {
              const settings = publication.track.mediaStreamTrack.getSettings();
              console.log(
                `✅ Streamer is publishing at: ${settings.width}x${
                  settings.height
                } @ ${Math.round(settings.frameRate || 0)}fps`
              );
            }
          });
        }
      }, 2000);
    } catch (error: any) {
      console.error('❌ Failed to enable camera and microphone:', error);
      const livekitError: LiveKitError = {
        type: 'device',
        message: 'Failed to enable camera and microphone',
        details: error,
      };
      this.handleError(livekitError);
      throw error;
    }
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    if (!this.room) return;

    this.room
      .on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
        console.log('👤 NEW PARTICIPANT JOINED:', {
          identity: participant.identity,
          name: participant.name,
          totalParticipants: this.room?.numParticipants || 0,
          localParticipant: this.room?.localParticipant?.identity,
        });
        this.events.onParticipantJoined?.(participant.identity);
      })
      .on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        console.log('👤 PARTICIPANT LEFT:', {
          identity: participant.identity,
          name: participant.name,
          totalParticipants: this.room?.numParticipants || 0,
        });
        this.events.onParticipantLeft?.(participant.identity);
      })
      .on(RoomEvent.Disconnected, (reason?: unknown) => {
        console.log('🔌 Room disconnected with reason:', reason);
        this.updateState('disconnected');
      })
      .on(RoomEvent.ConnectionStateChanged, (state) => {
        if (state === 'connected') {
          this.updateState('connected');
        } else if (state === 'disconnected') {
          this.updateState('disconnected');
        }
      })
      .on(RoomEvent.TrackSubscribed, (track, _publication, _participant) => {
        // Log received video quality for viewer-side diagnostics.
        if (track.kind === 'video' && track.mediaStreamTrack) {
          setTimeout(() => {
            const settings = track.mediaStreamTrack.getSettings();
            console.log(`📊 Viewer is receiving video at: ${settings.width}x${settings.height}`);
          }, 2000);
        }
      })
      .on(RoomEvent.TrackUnsubscribed, (track, _publication, participant) => {
        console.log(`📺 Track unsubscribed: ${track.kind} from ${participant.identity}`);
      })
      .on(RoomEvent.DataReceived, (_payload, participant) => {
        console.log('📡 Data received from participant:', participant?.identity);
      })
      .on(RoomEvent.RoomMetadataChanged, (metadata) => {
        console.log('🏠 Room metadata changed:', metadata);
      })
      .on(RoomEvent.SignalConnected, () => {
        console.log('📶 Signal connection established');
      })
      .on(RoomEvent.LocalTrackPublished, (publication) => {
        console.log('📤 Local track published:', publication.trackSid, publication.kind);
      })
      .on(RoomEvent.LocalTrackUnpublished, (publication) => {
        console.log('📤❌ Local track unpublished:', publication.trackSid, publication.kind);
      });
  }

  /**
   * Update state and trigger callback
   */
  private updateState(newState: LiveKitState): void {
    this.state = newState;
    this.events.onStateChange?.(newState);
  }

  /**
   * Handle errors
   */
  private handleError(error: LiveKitError): void {
    this.events.onError?.(error);
  }

  /**
   * Cleanup
   */
  dispose(): void {
    this.disconnect();
  }
}
